import MediaImage from "@/components/ui/MediaImage";
import { PropertyCardProps } from "@/cms/models";
import { getCaseUrl, getLabel } from "@/utils/helper";
import { getCompactPropertyFacts } from "@/utils/compact-property-facts";
import CompactPropertyFactValue from "./ui/CompactPropertyFactValue";
import Link from "next/link";
import PropertyLabel from "./ui/PropertyLabel";
import { useState } from "react";
import { SpinnerSvg } from "./icons/Spinner";
import Card from "./ui/Card";
import AddressComponent from "./ui/AddressComponent";

const PropertyGalleryCard: React.FC<
  PropertyCardProps & {
    shadow: "none" | "little" | "lots";
    lookSystem?: boolean;
    /** In a look-system card: its photo zooms by the section's motion level (src/styles/motion.css), not on its own. */
    cardPhoto?: boolean;
  }
> = ({ property, shadow, lookSystem, cardPhoto = false }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const imageUrl = property.images.find((x) => x.mdUrl)?.mdUrl;
  const label = getLabel(property);

  const { facts, emptyRow } = getCompactPropertyFacts(property);

  return (
    <Link
      aria-label={property.advertisementAddressOrDisplayName}
      onClick={() => setIsLoading(true)}
      href={getCaseUrl(property)}
      className="flex flex-col relative h-full justify-end"
      data-component-type="PropertyGalleryCard"
    >
      <Card shadow={shadow} lookSystem={lookSystem}>
        {isLoading && (
          <div className="absolute flex justify-center items-center inset-0 bg-gray-800 opacity-50 transition-opacity z-20">
            <SpinnerSvg />
          </div>
        )}

        <div className="overflow-hidden">
          <div className={`w-full relative ${label ? "min-h-8" : ""}`}>
            <div
              className={
                cardPhoto
                  ? "cursor-pointer h-[210px] w-full relative"
                  : "cursor-pointer hover:scale-110 transform duration-300 h-[210px] w-full relative"
              }
            >
              {imageUrl ? (
                <MediaImage
                  src={imageUrl}
                  alt={property.advertisementAddressOrDisplayName}
                  className="object-cover h-full w-full aspect-[16/9]"
                  data-re-card-photo={cardPhoto ? "" : undefined}
                />
              ) : (
                <div className="flex items-center justify-center h-full w-full bg-gray-200 aspect-[16/9]">
                  <span className="text-gray-500 text-lg">
                    Ingen billede tilgængeligt
                  </span>
                </div>
              )}
            </div>
            {label && (
              <PropertyLabel
                legacy={
                  <div className="absolute homepage-text-sm top-0 left-0 uppercase bg-[var(--label-bg-color)] text-[var(--label-text-color)] px-4 py-2 text-xs homepage-card-label-rounded-br">
                    <span>{label}</span>
                  </div>
                }
              >
                {label}
              </PropertyLabel>
            )}
          </div>
        </div>

        <div className="p-4 pb-0 flex flex-col homepage-text-sm">
          <AddressComponent property={property} />
          <ul className="w-full mt-4 text-base">
            {facts.map((fact) => (
              <li key={fact.key}>
                <div className="flex justify-between">
                  <p>{fact.label}</p>
                  <p>
                    <CompactPropertyFactValue fact={fact} />
                  </p>
                </div>
              </li>
            ))}
            {emptyRow && (
              <li>
                <div>&nbsp;</div>
              </li>
            )}
          </ul>
        </div>

        <div className="p-4">
          <div className=" border-t border-gray-300">
            <div className="mt-4 flex justify-end homepage-text-lg primary-font">
              <div>{property.priceInfo}</div>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default PropertyGalleryCard;
